// =============================================================================
// PEDRA — src/state/useOptimizer.ts
// Liga o estado do projeto ao otimizador (Worker ou fallback na thread).
//
// Regras:
//  - um calculo por vez: comecar um novo CANCELA o anterior;
//  - cancelar NUNCA vira erro, vira status 'cancelled';
//  - o handle e sempre cancelado ao desmontar, para nao vazar Worker;
//  - resultado de um job antigo que chegue atrasado e DESCARTADO (token de job).
//
// Este arquivo e .ts (nao .tsx) porque o contrato assim o define: o provider
// e montado com `createElement`, sem JSX.
// =============================================================================

import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';

import type {
  OptimizationInput,
  OptimizationResult,
  Project,
  ProgressEvent,
} from '../core/types';
import { validateProject } from '../core/validation';
import type { RunHandle } from '../optimizer/workerClient';
import { isCancelledError, runOptimization } from '../optimizer/workerClient';
import { useProject } from './store';

export type OptimizerStatus = 'idle' | 'running' | 'done' | 'cancelled' | 'error';

export interface OptimizerContextValue {
  status: OptimizerStatus;
  result: OptimizationResult | null;
  progress: ProgressEvent | null;
  error: string | null;
  elapsedMs: number;
  /** true quando o projeto mudou depois do ultimo calculo. */
  stale: boolean;
  start(): void;
  cancel(): void;
  clear(): void;
}

const OptimizerContext = createContext<OptimizerContextValue | null>(null);

/**
 * Copia defensiva do projeto para a entrada do otimizador. O resultado e um
 * SNAPSHOT: editar a tabela depois nao pode alterar o calculo em andamento,
 * nem o worker pode devolver objetos ligados ao estado da interface.
 */
function buildInput(project: Project): OptimizationInput {
  return {
    parts: project.parts.map((p) => ({ ...p, edgeBanding: { ...p.edgeBanding } })),
    stock: project.stock.map((s) => ({ ...s })),
    settings: { ...project.settings },
    edgeBands: project.edgeBands.map((b) => ({ ...b })),
  };
}

// -----------------------------------------------------------------------------
// PLANO GRAVADO NO NAVEGADOR
//
// O projeto ja volta inteiro depois de um F5 (autosave do <ProjectProvider>);
// o plano calculado tambem tem que voltar, senao a tela de resultado aparece
// vazia sem motivo. Guardamos o resultado junto de uma ASSINATURA das
// entradas: se qualquer entrada mudou desde o calculo, o plano gravado esta
// vencido e e simplesmente descartado na volta.
// -----------------------------------------------------------------------------

const RESULT_STORAGE_KEY = 'pedra.result.v1';

/** Acima disso nao vale a pena ocupar a cota do localStorage com o plano. */
const MAX_STORED_RESULT_CHARS = 2_000_000;

/**
 * true quando a linha nunca foi preenchida. A grade mantem sempre uma linha
 * vazia no fim de cada tabela e a recria depois de cada calculo — ela NAO e
 * uma mudanca de entrada e nao pode invalidar o plano.
 */
function isUntouchedRow(r: {
  length: number;
  width: number;
  label: string;
  material: string;
}): boolean {
  return r.length === 0 && r.width === 0 && r.label.trim() === '' && r.material.trim() === '';
}

/** Hash curto e estavel (djb2) da entrada do otimizador. */
function signatureOf(project: Project): string {
  const input = buildInput(project);
  const json = JSON.stringify({
    ...input,
    parts: input.parts.filter((p) => !isUntouchedRow(p)),
    stock: input.stock.filter((s) => !isUntouchedRow(s)),
  });
  let h = 5381;
  for (let i = 0; i < json.length; i += 1) {
    h = ((h << 5) + h + json.charCodeAt(i)) | 0;
  }
  return `${json.length.toString(36)}.${(h >>> 0).toString(36)}`;
}

/** Aceita como resultado apenas o que tem a forma esperada. */
function looksLikeResult(v: unknown): v is OptimizationResult {
  if (typeof v !== 'object' || v === null) return false;
  const r = v as { sheets?: unknown; unfit?: unknown; stats?: unknown };
  if (!Array.isArray(r.sheets) || !Array.isArray(r.unfit)) return false;
  if (typeof r.stats !== 'object' || r.stats === null) return false;
  return typeof (r.stats as { numSheets?: unknown }).numSheets === 'number';
}

/** Le o plano gravado. `null` quando nao ha, esta corrompido ou esta vencido. */
function loadStoredResult(project: Project): OptimizationResult | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = window.localStorage.getItem(RESULT_STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const box = parsed as { signature?: unknown; result?: unknown };
    if (box.signature !== signatureOf(project)) return null;
    return looksLikeResult(box.result) ? box.result : null;
  } catch {
    // Cota, modo anonimo ou JSON quebrado: o app abre sem plano, e so.
    return null;
  }
}

/** Grava o plano com a assinatura das entradas que o geraram. */
function saveStoredResult(project: Project, result: OptimizationResult): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const json = JSON.stringify({ signature: signatureOf(project), result });
    if (json.length > MAX_STORED_RESULT_CHARS) {
      window.localStorage.removeItem(RESULT_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(RESULT_STORAGE_KEY, json);
  } catch {
    // Sem espaco: o projeto (que importa) ja foi salvo pelo autosave.
  }
}

/** Apaga o plano gravado. */
function clearStoredResult(): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.removeItem(RESULT_STORAGE_KEY);
  } catch {
    /* ignora */
  }
}

export function OptimizerProvider(props: { children: ReactNode }): ReactElement {
  const { project } = useProject();

  /** Plano gravado na sessao anterior, se ainda combinar com este projeto. */
  const [restored] = useState<OptimizationResult | null>(() => loadStoredResult(project));

  const [status, setStatus] = useState<OptimizerStatus>(restored !== null ? 'done' : 'idle');
  const [result, setResult] = useState<OptimizationResult | null>(restored);
  const [progress, setProgress] = useState<ProgressEvent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(restored?.stats.elapsedMs ?? 0);
  const [stale, setStale] = useState(false);

  /** Execucao em andamento. */
  const handleRef = useRef<RunHandle | null>(null);
  /** Token do job atual: descarta respostas de execucoes ja abandonadas. */
  const jobRef = useRef(0);
  /**
   * Assinatura das entradas que geraram o resultado exibido. Comparamos
   * ASSINATURA (e nao a referencia do projeto) para que renomear o projeto ou
   * a linha em branco da grade nao envelhecam um plano que continua valido.
   */
  const computedSigRef = useRef<string | null>(restored !== null ? signatureOf(project) : null);

  /** Encerra o job atual e invalida qualquer resposta pendente dele. */
  const abandonCurrent = useCallback((): void => {
    jobRef.current += 1;
    const handle = handleRef.current;
    handleRef.current = null;
    if (handle) handle.cancel();
  }, []);

  const start = useCallback((): void => {
    // 1) Bloqueio por validacao: nem chega a criar worker.
    //    `validateProject` so devolve ERRO quando nao ha nada a calcular (sem
    //    peca valida, sem chapa valida, nenhuma peca cabendo em lugar nenhum).
    //    Peca grande demais, material sem chapa ou veio conflitante viram AVISO
    //    e seguem para a aba "Não couberam" — nunca barram o plano das outras.
    const { errors } = validateProject(project);
    if (errors.length > 0) {
      abandonCurrent();
      setStatus('error');
      setError(errors[0] ?? 'Não foi possível calcular o plano de corte.');
      setProgress(null);
      return;
    }

    // 2) Cancela o calculo anterior, se houver.
    if (handleRef.current !== null) {
      const previous = handleRef.current;
      handleRef.current = null;
      previous.cancel();
    }

    const job = jobRef.current + 1;
    jobRef.current = job;
    const snapshot = project;

    setStatus('running');
    setError(null);
    setProgress(null);
    setElapsedMs(0);

    let handle: RunHandle;
    try {
      handle = runOptimization(buildInput(snapshot), {
        onProgress: (e: ProgressEvent) => {
          if (jobRef.current !== job) return;
          setProgress(e);
          setElapsedMs(e.elapsedMs);
        },
      });
    } catch (e) {
      setStatus('error');
      setError(
        e instanceof Error
          ? `Não foi possível iniciar o cálculo: ${e.message}`
          : 'Não foi possível iniciar o cálculo.',
      );
      return;
    }

    handleRef.current = handle;

    handle.promise.then(
      (res) => {
        if (jobRef.current !== job) return;
        handleRef.current = null;
        computedSigRef.current = signatureOf(snapshot);
        saveStoredResult(snapshot, res);
        setResult(res);
        setElapsedMs(res.stats.elapsedMs);
        setStale(false);
        setStatus('done');
      },
      (e: unknown) => {
        if (jobRef.current !== job) return;
        handleRef.current = null;
        if (isCancelledError(e)) {
          setStatus('cancelled');
          return;
        }
        setStatus('error');
        setError(
          e instanceof Error && e.message.trim() !== ''
            ? `Falha no cálculo: ${e.message}`
            : 'Falha no cálculo do plano de corte.',
        );
      },
    );
  }, [project, abandonCurrent]);

  const cancel = useCallback((): void => {
    const handle = handleRef.current;
    if (handle === null) {
      // Nada rodando: so normaliza o status.
      setStatus((s) => (s === 'running' ? 'cancelled' : s));
      return;
    }
    handle.cancel();
  }, []);

  const clear = useCallback((): void => {
    abandonCurrent();
    clearStoredResult();
    computedSigRef.current = null;
    setStatus('idle');
    setResult(null);
    setProgress(null);
    setError(null);
    setElapsedMs(0);
    setStale(false);
  }, [abandonCurrent]);

  // O resultado envelhece quando as ENTRADAS mudam — e desenvelhece sozinho
  // se o usuario desfizer a mudanca (Ctrl+Z) e voltar ao estado calculado.
  useEffect(() => {
    const computed = computedSigRef.current;
    if (computed === null) return;
    setStale(signatureOf(project) !== computed);
  }, [project]);

  // Nunca deixar Worker vivo depois que o provider sai da arvore.
  useEffect(() => {
    return () => {
      jobRef.current += 1;
      const handle = handleRef.current;
      handleRef.current = null;
      if (handle) handle.cancel();
    };
  }, []);

  const value = useMemo<OptimizerContextValue>(
    () => ({ status, result, progress, error, elapsedMs, stale, start, cancel, clear }),
    [status, result, progress, error, elapsedMs, stale, start, cancel, clear],
  );

  return createElement(OptimizerContext.Provider, { value }, props.children);
}

/** Acessa o otimizador. Precisa estar dentro de <OptimizerProvider>. */
export function useOptimizer(): OptimizerContextValue {
  const ctx = useContext(OptimizerContext);
  if (ctx === null) {
    throw new Error('useOptimizer() precisa ser usado dentro de <OptimizerProvider>.');
  }
  return ctx;
}
